import { z } from "zod";
import { validateDateWindow, isContentActiveNow } from "./validation.js";
/** Optional UTC ISO 8601 schedule bounds (stored and transmitted as UTC). */
export const scheduleFieldsSchema = z.object({
    startsAt: z
        .union([z.string().datetime(), z.null()])
        .optional()
        .transform((v) => v ?? null),
    endsAt: z
        .union([z.string().datetime(), z.null()])
        .optional()
        .transform((v) => v ?? null),
});
export const DISPLAY_TIMEZONE = "Asia/Kabul";
export function parseScheduleDates(input) {
    return {
        startsAt: input.startsAt ? new Date(input.startsAt) : null,
        endsAt: input.endsAt ? new Date(input.endsAt) : null,
    };
}
export function toUtcIso(date) {
    if (!date)
        return null;
    return date.toISOString();
}
/** Determine content state at publish time. Fail-closed on invalid/expired windows. */
export function resolvePublishState(startsAt, endsAt, now = new Date()) {
    if (!validateDateWindow(startsAt, endsAt)) {
        return { ok: false, error: "End time must be after start time" };
    }
    if (endsAt && endsAt <= now) {
        return { ok: false, error: "End time must be in the future" };
    }
    if (startsAt && startsAt > now) {
        return { ok: true, state: "scheduled" };
    }
    return { ok: true, state: "published" };
}
/** Whether a published/scheduled item should appear on the public API now. */
export function isEligibleForPublicApi(startsAt, endsAt, now = new Date()) {
    return isContentActiveNow(startsAt, endsAt, now);
}
/** Lazy admin state refresh after schedule boundaries pass. */
export function resolveLifecycleState(currentState, startsAt, endsAt, now = new Date()) {
    if (currentState === "draft" ||
        currentState === "archived" ||
        currentState === "pending_approval" ||
        currentState === "paused") {
        return currentState;
    }
    if (endsAt && now >= endsAt)
        return "expired";
    if (currentState === "scheduled" && startsAt && now >= startsAt)
        return "published";
    return currentState;
}
/** Format UTC ISO for `<input type="datetime-local">` in Asia/Kabul. */
export function utcIsoToKabulDatetimeLocal(iso) {
    if (!iso)
        return "";
    const date = new Date(iso);
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: DISPLAY_TIMEZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? "00";
    return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
}
/** Parse datetime-local value interpreted as Asia/Kabul → UTC ISO. */
export function kabulDatetimeLocalToUtcIso(local) {
    const trimmed = local.trim();
    if (!trimmed)
        return null;
    const normalized = trimmed.length === 16 ? `${trimmed}:00` : trimmed;
    const ms = Date.parse(`${normalized}+04:30`);
    if (Number.isNaN(ms))
        return null;
    return new Date(ms).toISOString();
}
/** Display label for admin UI (LTR-isolated datetime). */
export function formatKabulDisplay(iso) {
    if (!iso)
        return "—";
    return new Intl.DateTimeFormat("fa-AF", {
        timeZone: DISPLAY_TIMEZONE,
        dateStyle: "medium",
        timeStyle: "short",
    }).format(new Date(iso));
}
//# sourceMappingURL=scheduling.js.map